import {
  Breadcrumb,
  Button,
  Card, Col, Form, ListGroup, Row
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import { Divider, Spinner, Stepper } from "@whitespace/whitespace-ui";
import { FlowOrigination } from "interfaces/FlowOrigination";
import { updateAssignHwrcPermissionsRequest } from "actions/updateAssignHwrcPermissionsRequest";
import { assignmentStepsHWRCRoleSite } from "modules/assignmentSteps";
import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import ISite from "interfaces/models/ISiteHwrc";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { mapClaimToSite } from "mappers/mapClaimToSite/mapClaimToSite";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IAssignHwrcPermissionsRequest from "interfaces/requests/IAssignHwrcPermissionsRequest";
import IClaimHwrc from "interfaces/responses/IClaimHwrc";
import { ClaimType } from "interfaces/models/ClaimType";
import UserSiteItem from "./UserSiteItem/UserSiteItem";

interface AssignUserSitesProps {
  flowOrigination : FlowOrigination
}

const AssignUserSites = ({ flowOrigination } : AssignUserSitesProps) => {
  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateAssignHwrcPermissionsRequest
  });
  const { state } = useStateMachine();
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const [sites, setSites] = useState<ISite[] | undefined>();
  const [ignoreClaimRemoval, setIgnoreClaimRemoval] = useState(false);
  const request : IAssignHwrcPermissionsRequest | undefined = state.assignHwrcPermissionsRequest;

  const {
    isSending: getSitesIsSending, errors: getSitesErrors, data: getSitesData, sendRequest: getSitesSendRequest
  } = useAmplifyREST();

  const {
    isSending: addClaimsIsSending, errors: addClaimsErrors, statusCode: addClaimsStatusCode, sendRequest: addClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: removeClaimsIsSending, errors: removeClaimsErrors, statusCode: removeClaimsStatusCode, sendRequest: removeClaimsSendRequest
  } = useAmplifyREST();

  const isSending = getSitesIsSending || addClaimsIsSending || removeClaimsIsSending;
  const errors = getSitesErrors || addClaimsErrors || removeClaimsErrors;

  const {
    register, handleSubmit, getValues, setValue, watch
  } = useForm({
    mode: "onSubmit"
  });

  useEffect(() => {
    // eslint-disable-next-line max-len
    const regionIds = state.assignHwrcPermissionsRequest!.regionIds.map(regionId => (`regions=${regionId}&`)).join("");
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/claim_types/hwrcsites/claims?${regionIds}`;
    getSitesSendRequest(url, "GET", null);
  }, [getSitesSendRequest]);

  useEffect(() => {
    if (getSitesData) {
      setSites(
        getSitesData.map(
          (claim : IClaimHwrc) => mapClaimToSite(claim)
        )
      );
    }
  }, [getSitesData]);

  useEffect(() => {
    if ((removeClaimsStatusCode === 204 || ignoreClaimRemoval) && addClaimsStatusCode === 204) {
      if (flowOrigination === FlowOrigination.UserManagement) {
        navigate("/manageuserapplications");
      } else {
        navigate("/requests");
      }
    }
  }, [removeClaimsStatusCode, addClaimsStatusCode]);

  const watchFields = watch(["hwrcSites"]);

  const renderSites = () => sites?.map((site : ISite, idx : number) => (
    <UserSiteItem register={register} key={`userSiteItem_${idx}`} site={site} />
  ));

  const constructRequestFromForm = useCallback(() => {
    if (state.assignHwrcPermissionsRequest) {
      const sitesSelected = getValues("hwrcSites") as string[];
      if (sitesSelected) {
        if (typeof sitesSelected === "string") {
          request!.siteIds.push(sitesSelected);
        } else {
          sitesSelected.forEach((siteId : string) => {
            request!.siteIds.push(siteId);
          });
        }
      }

      actions.updateAssignHwrcPermissionsRequest(request);
    }
  }, [getValues, state.assignWhitespacePermissionsRequest]);

  const setControlValues = useCallback(() => {
    if (getSitesData && state.assignHwrcPermissionsRequest!.siteIds.length > 0) {
      // Issue with React Hook forms where there is only one item
      if (!(getSitesData && getSitesData.length === 1
        && !state.assignHwrcPermissionsRequest!.siteIds.includes(getSitesData[0].id))) {
        setValue("hwrcSites", state.assignHwrcPermissionsRequest!.siteIds.map(String));
      }
    }
  }, [setValue, getSitesData]);

  useEffect(() => {
    let hwrcSitesSelected = getValues("hwrcSites") as string[];
    if (typeof hwrcSitesSelected === "string") {
      hwrcSitesSelected = [hwrcSitesSelected];
    }
    const areHwrcSitesSelected = hwrcSitesSelected && hwrcSitesSelected.length > 0;
    const assignedHwrcSiteIds : string[] = state.assignedClaimsHwrc!.filter((claim : IClaimHwrc) => claim.type === ClaimType.HwrcSite).map(claim => claim.id);
    const areHwrcSitesAltered = areHwrcSitesSelected
      && !(hwrcSitesSelected.every(item => assignedHwrcSiteIds.includes(item)) && assignedHwrcSiteIds.every(item => hwrcSitesSelected.includes(item)));
    setIsSaveButtonDisabled(!areHwrcSitesAltered);
  }, [watchFields]);

  useEffect(() => {
    setControlValues();
  }, [setControlValues]);

  const removeClaims = () => {
    const claimsToRemove = state.assignedClaimsHwrc!;

    if (claimsToRemove.length > 0) {
    // eslint-disable-next-line max-len
      const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
      removeClaimsSendRequest(url, "DELETE", claimsToRemove);
    } else {
      setIgnoreClaimRemoval(true);
    }
  };

  const addClaims = () => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
    let hwrcSitesSelected = getValues("hwrcSites") as string[];

    if (typeof hwrcSitesSelected === "string") {
      hwrcSitesSelected = [hwrcSitesSelected];
    }

    const claims : IClaimHwrc[] = [];

    const roleClaim : IClaimHwrc = {
      id: state.assignHwrcPermissionsRequest!.roleId!,
      displayName: "",
      type: ClaimType.Role
    };

    claims.push(roleClaim);

    state.assignHwrcPermissionsRequest?.regionIds.forEach((regionId : string) => {
      claims.push({
        id: regionId,
        displayName: "",
        type: ClaimType.Region
      });
    });

    hwrcSitesSelected.forEach((hwrcSiteId : string) => {
      claims.push((getSitesData.find((hwrcSiteClaim : IClaimHwrc) => hwrcSiteClaim.id === hwrcSiteId)));
    });

    addClaimsSendRequest(url, "PUT", claims);
  };

  useEffect(() => {
    if ((removeClaimsStatusCode === 204)) {
      addClaims();
    }
  }, [removeClaimsStatusCode]);

  const updateClaims = () => {
    removeClaims();
    addClaims();
  };

  const handleSaveClick = () => {
    constructRequestFromForm();
    updateClaims();
  };

  const handleBackButtonClick = () => {
    constructRequestFromForm();
    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/assignuserhwrcregions");
    } else {
      navigate("/requestsassignuserhwrcregions");
    }
  };

  return (
    <>
      {errors && <AmplifyError errors={errors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Assign Sites
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span data-testid="selected-user-title" className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form noValidate onSubmit={handleSubmit(handleSaveClick)}>
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={11} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  {flowOrigination === FlowOrigination.Requests
                  && (
                    <>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-requests"
                        linkAs={Link}
                        linkProps={{ to: "/requests" }}
                        onClick={() => navigate("/requests")}
                      >
                        Requests
                      </Breadcrumb.Item>
                      <Breadcrumb.Item data-testid="breadcrumb-site-url" active>
                        {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-roles"
                        linkAs={Link}
                        linkProps={{ to: "/requestsassignuserhwrcroles" }}
                        onClick={() => navigate("/requestsassignuserhwrcroles")}
                      >
                        Roles
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-regions"
                        linkAs={Link}
                        linkProps={{ to: "/requestsassignuserhwrcregions" }}
                        onClick={() => navigate("/requestsassignuserhwrcregions")}
                      >
                        Regions
                      </Breadcrumb.Item>
                      <Breadcrumb.Item data-testid="breadcrumb-hwrcsites" active>
                        Sites
                      </Breadcrumb.Item>
                    </>
                  )}
                  {flowOrigination === FlowOrigination.UserManagement
                  && (
                    <>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-users"
                        linkAs={Link}
                        linkProps={{ to: "/users" }}
                        onClick={() => navigate("/users")}
                      >
                        Users
                      </Breadcrumb.Item>

                      <Breadcrumb.Item
                        data-testid="breadcrumb-manage-user"
                        linkAs={Link}
                        linkProps={{ to: "/manageuser" }}
                        onClick={() => navigate("/manageuser")}
                      >
                        Manage User
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-manage-user-applications"
                        linkAs={Link}
                        linkProps={{ to: "/manageuserapplications" }}
                        onClick={() => navigate("/manageuserapplications")}
                      >
                        Applications
                      </Breadcrumb.Item>
                      <Breadcrumb.Item data-testid="breadcrumb-site-url" active>
                        {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-roles"
                        linkAs={Link}
                        linkProps={{ to: "/assignuserhwrcroles" }}
                        onClick={() => navigate("/assignuserhwrcroles")}
                      >
                        Roles
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-regions"
                        linkAs={Link}
                        linkProps={{ to: "/assignuserhwrcregions" }}
                        onClick={() => navigate("/assignuserhwrcregions")}
                      >
                        Regions
                      </Breadcrumb.Item>
                      <Breadcrumb.Item data-testid="breadcrumb-hwrcsites" active>
                        Sites
                      </Breadcrumb.Item>
                    </>
                  )}
                </Breadcrumb>
              </Col>
              <Col xs={1} className="d-flex justify-content-end">
                <Button data-testid="btnSave" variant="primary" type="submit" disabled={isSaveButtonDisabled}>
                  Save
                </Button>
              </Col>
            </Row>
            <Divider />
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <span data-testid="stepper-container" className="w-100">
                  <Stepper className="w-100 ms-4 mb-0" steps={assignmentStepsHWRCRoleSite} currentStep={2} />
                </span>
              </Col>
              <Col xs={4} className="d-flex justify-content-end">
                <Button variant="secondary" onClick={() => handleBackButtonClick()}>
                  Back
                </Button>
              </Col>
            </Row>
            <Divider />
            <ListGroup variant="flush" className="striped highlight">
              {renderSites()}
            </ListGroup>
          </Form>

        </Card.Body>
      </Card>
    </>
  );
};

export default AssignUserSites;
